import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createAvalLineApi,
  deleteAvalLineApi,
  fetchAccounts,
  fetchAvalLines,
  fetchCompanies,
  patchAvalLine,
  type Account,
  type AvalLine,
  type AvalLineWithStats,
  type AvalPeriodicity,
  type Company,
} from "../api";
import { formatIban } from "../lib/grouping";

/**
 * Listado de LÍNEAS DE AVALES. Una línea agrupa avales bajo un mismo
 * contrato-marco con un banco. Los % de comisión, CECO y proyecto viven
 * en cada AVAL individual (fase siguiente), pero aquí se guardan valores
 * por defecto que se prerellenan al crear un aval nuevo.
 */

const PERIODICITY_LABELS: Record<AvalPeriodicity, string> = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

function fmtMoney(n: number, currency = "EUR"): string {
  return n.toLocaleString("es-ES", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AvalLines() {
  const [items, setItems] = useState<AvalLineWithStats[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    companyId: "",
    iban: "",
    bankCode: "",
    bankName: "",
    lineNumber: "",
    alias: "",
    currency: "EUR",
    lineAmount: "",
    defaultOpeningRatePercent: "",
    defaultCommissionRateAnnual: "",
    defaultPeriodicity: "quarterly" as AvalPeriodicity,
    defaultCommissionMinimum: "",
    sapAccountExpense: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  /** Empresas colapsadas (companyId). Persistido en sessionStorage. */
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem("avalLines.collapsedCompanies");
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* noop */ }
    return new Set();
  });
  const persistCollapsed = (next: Set<string>) => {
    setCollapsedCompanies(next);
    try {
      sessionStorage.setItem("avalLines.collapsedCompanies", JSON.stringify(Array.from(next)));
    } catch { /* noop */ }
  };
  const toggleCollapsed = (companyId: string) => {
    const next = new Set(collapsedCompanies);
    if (next.has(companyId)) next.delete(companyId);
    else next.add(companyId);
    persistCollapsed(next);
  };

  const load = async () => {
    setError(null);
    try {
      const [l, c, a] = await Promise.all([fetchAvalLines(), fetchCompanies(), fetchAccounts()]);
      setItems(l.items);
      setCompanies(c.items);
      setAccounts(a.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const companyById = useMemo(
    () => new Map(companies.map((c) => [c.id, c])),
    [companies],
  );

  /**
   * Líneas agrupadas por empresa. Cada grupo contiene la empresa (o shim si
   * no aparece en el maestro) y sus líneas ordenadas por bankCode+lineNumber.
   * Los grupos se ordenan alfabéticamente por alias||name.
   */
  const groupedByCompany = useMemo(() => {
    if (!items) return null;
    const byId = new Map<
      string,
      { company: Company | { id: string; name: string; alias: string }; items: AvalLineWithStats[] }
    >();
    for (const l of items) {
      const key = l.companyId || "__no_company__";
      const existing = byId.get(key);
      if (existing) {
        existing.items.push(l);
      } else {
        const company =
          companyById.get(l.companyId) ??
          ({ id: l.companyId || "", name: l.companyId || "(sin empresa)", alias: "" } as {
            id: string;
            name: string;
            alias: string;
          });
        byId.set(key, { company, items: [l] });
      }
    }
    for (const g of byId.values()) {
      g.items.sort((a, b) =>
        (a.bankCode + a.lineNumber).localeCompare(b.bankCode + b.lineNumber),
      );
    }
    return Array.from(byId.values()).sort((a, b) => {
      const la = (a.company.alias || a.company.name || a.company.id).toLowerCase();
      const lb = (b.company.alias || b.company.name || b.company.id).toLowerCase();
      return la.localeCompare(lb, "es");
    });
  }, [items, companyById]);

  const accountsForCompany = useMemo(() => {
    if (!draft.companyId) return [] as Account[];
    return accounts.filter((a) => a.active && a.companyId === draft.companyId);
  }, [accounts, draft.companyId]);

  const onSelectAccount = (iban: string) => {
    const acc = accounts.find((a) => a.iban === iban);
    if (!acc) {
      setDraft({ ...draft, iban: "", bankCode: "", bankName: "" });
      return;
    }
    setDraft({
      ...draft,
      iban: acc.iban,
      bankCode: acc.bankCode,
      bankName: acc.bankName,
      currency: acc.currency || "EUR",
    });
  };

  const parseNumOrNull = (s: string): number | null => {
    const t = s.trim().replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    if (!draft.companyId) return alert("Elige empresa.");
    if (!draft.iban || !draft.bankCode) return alert("Elige la cuenta bancaria de cargo.");
    if (!draft.lineNumber.trim()) return alert("Indica el número de línea.");
    setBusy(true);
    try {
      await createAvalLineApi({
        bankCode: draft.bankCode,
        lineNumber: draft.lineNumber.trim(),
        companyId: draft.companyId,
        alias: draft.alias.trim() || undefined,
        bankName: draft.bankName.trim() || undefined,
        iban: draft.iban,
        currency: draft.currency,
        notes: draft.notes.trim() || undefined,
        lineAmount: parseNumOrNull(draft.lineAmount),
        defaultOpeningRatePercent: parseNumOrNull(draft.defaultOpeningRatePercent),
        defaultCommissionRateAnnual: parseNumOrNull(draft.defaultCommissionRateAnnual),
        defaultPeriodicity: draft.defaultPeriodicity,
        defaultCommissionMinimum: parseNumOrNull(draft.defaultCommissionMinimum),
        sapAccountExpense: draft.sapAccountExpense.trim() || undefined,
      });
      setDraft({
        companyId: "",
        iban: "",
        bankCode: "",
        bankName: "",
        lineNumber: "",
        alias: "",
        currency: "EUR",
        lineAmount: "",
        defaultOpeningRatePercent: "",
        defaultCommissionRateAnnual: "",
        defaultPeriodicity: "quarterly",
        defaultCommissionMinimum: "",
        sapAccountExpense: "",
        notes: "",
      });
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const commitLineField = async (
    line: AvalLine,
    patch: Partial<AvalLine>,
  ) => {
    // Optimistic: parchea local para que la UI no parpadee y luego persiste.
    setItems((prev) =>
      prev ? prev.map((l) => (l.bankCode === line.bankCode && l.lineNumber === line.lineNumber ? { ...l, ...patch } : l)) : prev,
    );
    try {
      await patchAvalLine(line.bankCode, line.lineNumber, patch);
    } catch (e: unknown) {
      alert(`Error al guardar: ${e instanceof Error ? e.message : String(e)}`);
      load();
    }
  };

  const onDelete = async (line: AvalLine) => {
    const label = line.alias || `${line.bankCode}/${line.lineNumber}`;
    if (!confirm(`¿Borrar la línea "${label}"?`)) return;
    try {
      await deleteAvalLineApi(line.bankCode, line.lineNumber);
      load();
    } catch (e: unknown) {
      alert(`No se ha podido borrar: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Líneas de Avales</h2>
          <p className="meta">
            Contratos-marco con el banco. Cada línea agrupa avales concretos que se
            gestionan dentro de ella (nº aval, importe, receptor, CECO, proyecto y
            cuadro de comisiones futuras hasta 31/12/2028 si no hay vencimiento).
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            onClick={() => {
              if (!groupedByCompany) return;
              const allIds = groupedByCompany.map((g) => g.company.id || "__no_company__");
              const anyExpanded = allIds.some((id) => !collapsedCompanies.has(id));
              persistCollapsed(anyExpanded ? new Set(allIds) : new Set());
            }}
            title="Colapsar o expandir todas las empresas"
          >
            {groupedByCompany && groupedByCompany.every((g) => collapsedCompanies.has(g.company.id || "__no_company__"))
              ? "▸ Expandir todas"
              : "▾ Colapsar todas"}
          </button>
          <button type="button" onClick={() => setCreating((x) => !x)} disabled={companies.length === 0}>
            {creating ? "✕ Cancelar" : "+ Nueva línea"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {companies.length === 0 && (
        <p className="warning">No hay empresas. Crea al menos una en "Empresas" para asociar líneas de avales.</p>
      )}

      {creating && companies.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.75rem 0" }}>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em" }}>
              Empresa<br />
              <select
                value={draft.companyId}
                onChange={(e) => setDraft({ ...draft, companyId: e.target.value, iban: "", bankCode: "", bankName: "" })}
              >
                <option value="">— Elige —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Cuenta bancaria de cargo<br />
              <select
                value={draft.iban}
                onChange={(e) => onSelectAccount(e.target.value)}
                disabled={!draft.companyId}
                style={{ minWidth: "18rem" }}
              >
                <option value="">— Elige cuenta —</option>
                {accountsForCompany.map((a) => (
                  <option key={a.iban} value={a.iban}>
                    {formatIban(a.iban)} · {a.bankCode}{a.alias ? ` · ${a.alias}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Nº línea<br />
              <input value={draft.lineNumber} onChange={(e) => setDraft({ ...draft, lineNumber: e.target.value })} style={{ width: "10rem" }} />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Alias<br />
              <input value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} style={{ width: "12rem" }} />
            </label>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: "0.85em" }}>
              Importe línea<br />
              <input value={draft.lineAmount} onChange={(e) => setDraft({ ...draft, lineAmount: e.target.value })} style={{ width: "10rem" }} placeholder="p. ej. 1.000.000" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              % apertura (default)<br />
              <input value={draft.defaultOpeningRatePercent} onChange={(e) => setDraft({ ...draft, defaultOpeningRatePercent: e.target.value })} style={{ width: "6rem" }} placeholder="0,10" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              % anual comisión (default)<br />
              <input value={draft.defaultCommissionRateAnnual} onChange={(e) => setDraft({ ...draft, defaultCommissionRateAnnual: e.target.value })} style={{ width: "6rem" }} placeholder="0,40" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Periodicidad (default)<br />
              <select value={draft.defaultPeriodicity} onChange={(e) => setDraft({ ...draft, defaultPeriodicity: e.target.value as AvalPeriodicity })}>
                {(Object.keys(PERIODICITY_LABELS) as AvalPeriodicity[]).map((k) => (
                  <option key={k} value={k}>{PERIODICITY_LABELS[k]}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }} title="Mínimo anual (se prorratea por período)">
              Mín. anual comisión (default)<br />
              <input value={draft.defaultCommissionMinimum} onChange={(e) => setDraft({ ...draft, defaultCommissionMinimum: e.target.value })} style={{ width: "8rem" }} placeholder="opcional" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Cta. gasto SAP<br />
              <input
                value={draft.sapAccountExpense}
                onChange={(e) => setDraft({ ...draft, sapAccountExpense: e.target.value })}
                style={{ width: "10rem" }}
                placeholder="p. ej. 626000000"
              />
            </label>
            <label style={{ fontSize: "0.85em", flex: 1, minWidth: "16rem" }}>
              Notas<br />
              <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} style={{ width: "100%" }} />
            </label>
          </div>

          <div>
            <button type="button" onClick={save} disabled={busy}>{busy ? "Guardando…" : "Guardar línea"}</button>
          </div>
        </div>
      )}

      {error && <p className="error">Error: {error}</p>}
      {groupedByCompany === null && <p>Cargando…</p>}
      {groupedByCompany && groupedByCompany.length === 0 && !creating && (
        <p className="meta">No hay líneas todavía. Crea una con "+ Nueva línea".</p>
      )}

      {groupedByCompany && groupedByCompany.length > 0 && (
        <>
          <style>{`
            .lines-tbl { font-size: 0.8em; width: 100%; table-layout: fixed; border-collapse: collapse; }
            .lines-tbl th, .lines-tbl td { padding: 4px 6px; vertical-align: middle; border-bottom: 1px solid var(--border, #e5e7eb); }
            .lines-tbl th { font-weight: 600; text-align: left; background: rgba(120,150,200,0.06); }
            .lines-tbl input, .lines-tbl select { width: 100%; padding: 2px 4px; font-size: inherit; box-sizing: border-box; border: 1px solid transparent; border-radius: 3px; background: transparent; }
            .lines-tbl input:hover, .lines-tbl select:hover { border-color: var(--border, #ccd); background: rgba(0,0,0,0.02); }
            .lines-tbl input:focus, .lines-tbl select:focus { border-color: #6a8fc9; background: #fff; outline: none; }
            .lines-tbl .num { text-align: right; font-variant-numeric: tabular-nums; }
            .lines-tbl .ellip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .lines-tbl .lineno { font-family: ui-monospace, "Consolas", monospace; font-size: 0.92em; word-break: break-all; line-height: 1.1; }
            .lines-tbl .usage-bar { height: 4px; background: var(--border, #e5e7eb); border-radius: 2px; overflow: hidden; margin-top: 2px; }
            .lines-tbl .usage-bar > div { height: 100%; background: #6a8fc9; }
            .lines-tbl .usage-bar > div.warn { background: #d97706; }
            .lines-tbl .usage-bar > div.over { background: #dc2626; }
            .lines-tbl .row-actions button { padding: 2px 6px; font-size: 0.9em; }
          `}</style>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {groupedByCompany.map((g) => {
            const displayName = g.company.alias || g.company.name || g.company.id;
            const key = g.company.id || "__no_company__";
            const collapsed = collapsedCompanies.has(key);
            return (
              <div key={key} className="card owner-card">
                <div className="card-head">
                  <div
                    onClick={() => toggleCollapsed(key)}
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
                    title={collapsed ? "Expandir" : "Colapsar"}
                  >
                    <span style={{ fontSize: "0.9em", width: 14, display: "inline-block" }}>
                      {collapsed ? "▸" : "▾"}
                    </span>
                    <div>
                      <h3 style={{ margin: 0 }}>🏢 {displayName}</h3>
                      <p className="meta" style={{ margin: "4px 0 0 0" }}>
                        {g.items.length} línea{g.items.length === 1 ? "" : "s"} de avales
                      </p>
                    </div>
                  </div>
                </div>
                {!collapsed && (
                <div style={{ overflowX: "auto", width: "100%", marginTop: 8 }}>
                  <table className="lines-tbl">
                    <colgroup>
                      <col style={{ width: "5.5rem" }} />
                      <col style={{ width: "10rem" }} />
                      <col />
                      <col style={{ width: "12rem" }} />
                      <col style={{ width: "8rem" }} />
                      <col style={{ width: "9rem" }} />
                      <col style={{ width: "3.8rem" }} />
                      <col style={{ width: "3.8rem" }} />
                      <col style={{ width: "5.5rem" }} />
                      <col style={{ width: "5rem" }} />
                      <col style={{ width: "7rem" }} />
                      <col style={{ width: "4.5rem" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Banco</th>
                        <th>Nº línea</th>
                        <th>Alias</th>
                        <th>IBAN cargo</th>
                        <th className="num">Importe línea</th>
                        <th className="num">Suma avales (vig.)</th>
                        <th className="num">% ap.</th>
                        <th className="num">% an.</th>
                        <th>Periodic.</th>
                        <th className="num" title="Mínimo anual (se prorratea por período)">Mín./año</th>
                        <th>Cta. gasto</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((l) => {
                        const accs = accounts.filter((a) => a.companyId === l.companyId);
                const sumVigente = l.stats?.sumVigente ?? 0;
                const usagePct = l.lineAmount && l.lineAmount > 0
                  ? Math.round((sumVigente / l.lineAmount) * 1000) / 10
                  : null;
                const usageCls = usagePct === null ? "" : usagePct > 100 ? "over" : usagePct > 85 ? "warn" : "";
                return (
                  <tr key={`${l.bankCode}__${l.lineNumber}`}>
                    <td className="ellip" title={l.bankName || l.bankCode}>{l.bankName || l.bankCode}</td>
                    <td className="lineno">
                      <Link to={`/avales/${encodeURIComponent(l.bankCode)}/${encodeURIComponent(l.lineNumber)}`}>
                        {l.lineNumber}
                      </Link>
                    </td>
                    <td>
                      <input
                        defaultValue={l.alias}
                        onBlur={(e) => e.target.value !== l.alias && commitLineField(l, { alias: e.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        value={l.iban}
                        onChange={(e) => {
                          const acc = accounts.find((a) => a.iban === e.target.value);
                          commitLineField(l, {
                            iban: e.target.value,
                            bankCode: acc?.bankCode ?? l.bankCode,
                            bankName: acc?.bankName ?? l.bankName,
                          });
                        }}
                      >
                        <option value="">— sin cuenta —</option>
                        {accs.map((a) => (
                          <option key={a.iban} value={a.iban}>{formatIban(a.iban)}{a.alias ? ` · ${a.alias}` : ""}</option>
                        ))}
                        {l.iban && !accs.some((a) => a.iban === l.iban) && (
                          <option value={l.iban}>{formatIban(l.iban)} (no en maestro)</option>
                        )}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        className="num"
                        defaultValue={l.lineAmount ?? ""}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = parseNumOrNull(e.target.value);
                          if (v !== l.lineAmount) commitLineField(l, { lineAmount: v });
                        }}
                      />
                    </td>
                    <td className="num">
                      <div title={`${l.stats?.countVigente ?? 0} vigente(s) / ${l.stats?.count ?? 0} total`}>
                        {fmtMoney(sumVigente, l.currency)}
                        {usagePct !== null && (
                          <>
                            <div style={{ fontSize: "0.85em", color: "var(--muted, #666)" }}>{usagePct}% de la línea</div>
                            <div className="usage-bar"><div className={usageCls} style={{ width: `${Math.min(100, usagePct)}%` }} /></div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="num">
                      <input
                        className="num"
                        defaultValue={l.defaultOpeningRatePercent ?? ""}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = parseNumOrNull(e.target.value);
                          if (v !== l.defaultOpeningRatePercent) commitLineField(l, { defaultOpeningRatePercent: v });
                        }}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="num"
                        defaultValue={l.defaultCommissionRateAnnual ?? ""}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = parseNumOrNull(e.target.value);
                          if (v !== l.defaultCommissionRateAnnual) commitLineField(l, { defaultCommissionRateAnnual: v });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={l.defaultPeriodicity}
                        onChange={(e) => commitLineField(l, { defaultPeriodicity: e.target.value as AvalPeriodicity })}
                      >
                        {(Object.keys(PERIODICITY_LABELS) as AvalPeriodicity[]).map((k) => (
                          <option key={k} value={k}>{PERIODICITY_LABELS[k]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        className="num"
                        defaultValue={l.defaultCommissionMinimum ?? ""}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = parseNumOrNull(e.target.value);
                          if (v !== l.defaultCommissionMinimum) commitLineField(l, { defaultCommissionMinimum: v });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        defaultValue={l.sapAccountExpense}
                        placeholder="—"
                        title="Cuenta contable de gasto (usada al contabilizar comisiones desde el banco)"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== l.sapAccountExpense) commitLineField(l, { sapAccountExpense: v });
                        }}
                      />
                    </td>
                    <td className="row-actions">
                      <button type="button" onClick={() => onDelete(l)} title="Borrar línea (solo si no tiene avales)">Borrar</button>
                    </td>
                  </tr>
                );
              })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}
    </section>
  );
}
